import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  BarChart3,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock3,
  FileText,
  Flag,
  LayoutDashboard,
  LayoutGrid,
  Layers,
  Link2,
  List,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Video,
  X,
} from 'lucide-react';
import type {
  CalendarEntry,
  Campaign,
  Content,
  Proposal,
  SourceRef,
  SourceRevision,
  Stage,
  State,
  SessionInfo,
} from '../shared/model';
import { formats, stages, stageLabels } from '../shared/model';
import { Brand, FormatBadge, formatClass } from './identity';
import { Dashboard } from './Dashboard';
import { Login, MetaConnection, Metrics, metaLabel, useFirstAccountRead } from './Meta';
import { Publications } from './Publications';
import { CampaignWorkspace } from './CampaignWorkspace';
import { publicationOrigin } from './PublishQueue';
import { EmailLoginLink } from './EmailLogin';
import { ReconciliationWorkspace } from './ReconciliationWorkspace';
import { ExportChangesWorkspace } from './ExportChangesWorkspace';
import { TeamSettings, CollectionSettings } from './TeamSettings';
import { operationalCalendar } from '../shared/campaigns';
import { CalendarWorkspace } from './CalendarWorkspace';

type Page =
  'overview' | 'campaigns' | 'content' | 'calendar' | 'metrics' | 'connections' | 'publications';
type Modal =
  | { kind: 'campaign'; item?: Campaign }
  | { kind: 'content'; item?: Content; campaignId?: string }
  | { kind: 'date'; item?: Content; date?: string }
  | { kind: 'publication'; item: Content }
  | { kind: 'source'; source: SourceRef }
  | { kind: 'history'; entry: CalendarEntry; label: string; reference?: boolean };
const pages = {
  overview: 'Visão geral',
  calendar: 'Calendário',
  content: 'Conteúdo',
  publications: 'Publicações',
  campaigns: 'Campanhas',
  metrics: 'Métricas',
  connections: 'Conexões',
};
const icons = {
  overview: LayoutDashboard,
  campaigns: Flag,
  content: Layers,
  publications: Video,
  calendar: CalendarDays,
  metrics: BarChart3,
  connections: Link2,
};
const dateLabel = (date: string, long = false) =>
  date
    ? new Date(`${date}T12:00:00`)
        .toLocaleDateString(
          'pt-BR',
          long
            ? { day: 'numeric', month: 'long', year: 'numeric' }
            : { day: '2-digit', month: 'short' },
        )
        .replace('.', '')
    : 'Sem data';
const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
const initials = (name: string) =>
  name
    ? name
        .split(' ')
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase()
    : '—';
const s = (form: FormData, key: string) => String(form.get(key) ?? '');
const proposalLabels: Record<Proposal['status'], string> = {
  pendente: 'Em revisão',
  aprovada: 'Aprovada · registro no acervo pendente',
  substituida: 'Substituída por nova data',
  executada: 'Publicação registrada',
  rejeitada: 'Rejeitada',
  conflito: 'Conflito · revisar a base',
  incorporada: 'Incorporada ao acervo',
};
function Badge({ children, tone = '' }: { children: ReactNode; tone?: string }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}
function StageBadge({ stage }: { stage: Stage }) {
  return <Badge tone={stage}>{stageLabels[stage]}</Badge>;
}
function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <FileText size={30} />
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={wide ? 'field wide' : 'field'}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export function App() {
  const [data, setData] = useState<State>();
  const [session, setSession] = useState<SessionInfo>();
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [route, setRoute] = useState(location.hash);
  const [page, setPage] = useState<Page>(() => {
    const hash = location.hash.slice(1).split('?')[0];
    return hash in pages ? (hash as Page) : 'overview';
  });
  const [modal, setModal] = useState<Modal>();
  const [search, setSearch] = useState('');
  const [campaign, setCampaign] = useState('');
  const [format, setFormat] = useState('');
  const [stage, setStage] = useState('');
  const [owner, setOwner] = useState('');
  const [board, setBoard] = useState(true);
  const [showDiscarded, setShowDiscarded] = useState(false);
  async function refresh() {
    const sessionResponse = await fetch('/api/session');
    if (!sessionResponse.ok) throw new Error('O FLG não respondeu. Tente novamente.');
    const nextSession = (await sessionResponse.json()) as SessionInfo;
    setSession(nextSession);
    if (!nextSession.authenticated) {
      setData(undefined);
      return;
    }
    const response = await fetch('/api/state');
    if (!response.ok) throw new Error('O FLG não respondeu. Tente novamente.');
    const state = (await response.json()) as State;
    setData(state);
    return state;
  }
  useEffect(() => {
    refresh().catch((e) => setError(e.message));
    const listener = () => {
      const key = location.hash.slice(1).split('?')[0];
      setRoute(location.hash);
      if (key in pages) setPage(key as Page);
    };
    window.addEventListener('hashchange', listener);
    return () => window.removeEventListener('hashchange', listener);
  }, []);
  const readingAccounts = useFirstAccountRead(data?.integration, data?.csrf, refresh);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 6500);
    return () => clearTimeout(timer);
  }, [toast]);
  function navigate(next: Page) {
    location.hash = next;
    setRoute(`#${next}`);
    setPage(next);
    setError('');
  }
  function openCampaignWorkspace(
    id: string,
    tab?: string,
    packageId?: string,
    automationId?: string,
  ) {
    const next = `#campaigns?id=${encodeURIComponent(id)}${tab ? `&tab=${encodeURIComponent(tab)}` : ''}${packageId ? `&packageId=${encodeURIComponent(packageId)}` : ''}${automationId ? `&automationId=${encodeURIComponent(automationId)}` : ''}`;
    location.hash = next;
    setRoute(next);
    setPage('campaigns');
    setError('');
  }
  function open(value: Modal) {
    setError('');
    setModal(value);
  }
  async function action(
    path: string,
    body: unknown,
    message: string,
    method = 'POST',
    close = true,
  ) {
    if (busy || !data) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Não foi possível salvar.');
      await refresh();
      if (close) setModal(undefined);
      setToast(message);
      return result;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (location.pathname === '/auth/email' && session) return <EmailLoginLink session={session} />;
  if (session && session.mode !== 'local' && !session.authenticated)
    return <Login session={session} />;
  if (!data)
    return (
      <main className="boot">
        <div className="brand">
          <Brand />
        </div>
        <h1>Aplicativo.Net</h1>
        {error ? (
          <>
            <p role="alert">{error}</p>
            <button
              onClick={() => {
                setError('');
                refresh().catch((e) => setError(e.message));
              }}
            >
              Tentar novamente
            </button>
          </>
        ) : (
          <p>
            <Loader2 className="spin" size={18} /> Abrindo seu espaço de trabalho…
          </p>
        )}
      </main>
    );
  const current = data.calendars.find((c) => c.kind === 'vigente');
  const dateOf = (item: Content) =>
    operationalCalendar(data).find((e) => e.contentId === item.id)?.date ?? '';
  const selectedCampaign = data.campaigns.find(
    (c) => c.id === new URLSearchParams(route.split('?')[1] ?? '').get('id'),
  );
  const isCampaignDetail = page === 'campaigns' && Boolean(selectedCampaign);
  const publishedContentIds = new Set(
    data.publications.map((publication) => publication.contentId),
  );
  const eligible = data.contents.filter(
    (c) => !['publicado', 'medido', 'descartado'].includes(c.stage),
  );
  const filtered = data.contents.filter(
    (c) =>
      (!search ||
        `${c.title} ${c.flgId ?? ''} ${c.owner}`
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase())) &&
      (!campaign || c.campaignIds.includes(campaign)) &&
      (!format || c.format === format) &&
      (!stage ||
        (stage === 'production'
          ? !['publicado', 'medido', 'descartado'].includes(c.stage)
          : c.stage === stage)) &&
      (!owner || (owner === 'none' ? !c.owner : c.owner === owner)) &&
      (showDiscarded || stage === 'descartado' || c.stage !== 'descartado'),
  );
  const pending = data.proposals.filter((p) => ['pendente', 'conflito'].includes(p.status));
  const owners = [...new Set(data.contents.map((c) => c.owner).filter(Boolean))];
  const contentCard = (item: Content, compact = false) => (
    <button
      key={item.id}
      className={`content-card ${compact ? 'compact' : ''}`}
      onClick={() => open({ kind: 'content', item })}
      draggable={!['publicado', 'medido', 'descartado'].includes(item.stage)}
      onDragStart={(e) => e.dataTransfer.setData('text/flg-content', item.id)}
    >
      <div className="card-meta">
        <span>
          {item.flgId ?? 'PAUTA'} <FormatBadge format={item.format} />
        </span>
        {item.reserve && <Badge>Reserva</Badge>}
      </div>
      <h3>{item.title}</h3>
      {item.campaignIds.length > 0 && (
        <div className="campaign-tag">
          <Flag size={11} />
          {data.campaigns.find((c) => c.id === item.campaignIds[0])?.name}
          {item.campaignIds.length > 1 ? ` +${item.campaignIds.length - 1}` : ''}
        </div>
      )}
      <div className="card-foot">
        <span>
          <CalendarDays size={13} />
          {dateLabel(dateOf(item))}
        </span>
        <span className="card-owner">
          <span className="avatar" title={item.owner || 'Responsável a definir'}>
            {initials(item.owner)}
          </span>
          {!item.owner && <small>A definir</small>}
        </span>
      </div>
      {item.conflict && (
        <span className="conflict-label">
          <CircleAlert size={12} />
          Conflito com o acervo
        </span>
      )}
      {!item.source && <span className="local-label">Pauta local · sem ID FLG</span>}
    </button>
  );
  const filters = (
    <div className="filters">
      <label className="search">
        <Search size={16} />
        <input
          aria-label="Buscar conteúdo"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar título ou ID…"
        />
      </label>
      <select
        aria-label="Filtrar campanha"
        value={campaign}
        onChange={(e) => setCampaign(e.target.value)}
      >
        <option value="">Todas as campanhas</option>
        {data.campaigns.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        aria-label="Filtrar formato"
        value={format}
        onChange={(e) => setFormat(e.target.value)}
      >
        <option value="">Todos os formatos</option>
        {formats.map((f) => (
          <option key={f}>{f}</option>
        ))}
      </select>
      <select aria-label="Filtrar etapa" value={stage} onChange={(e) => setStage(e.target.value)}>
        <option value="">Todas as etapas</option>
        <option value="production">Em produção</option>
        {stages.map((st) => (
          <option key={st} value={st}>
            {stageLabels[st]}
          </option>
        ))}
      </select>
      <select
        aria-label="Filtrar responsável"
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
      >
        <option value="">Responsáveis</option>
        <option value="none">A definir</option>
        {owners.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
      {(search || campaign || format || stage || owner) && (
        <button
          className="text-button"
          onClick={() => {
            setSearch('');
            setCampaign('');
            setFormat('');
            setStage('');
            setOwner('');
          }}
        >
          Limpar
        </button>
      )}
    </div>
  );

  return (
    <div className="shell">
      <aside className="sidebar">
        <a href="#overview" className="brand" aria-label="FLG Reinaldo Ferreira, visão geral">
          <Brand />
        </a>
        <div className="workspace-card">
          <strong>Aplicativo.Net</strong>
          <span>Workspace de marketing</span>
        </div>
        <div className="workspace-label">OPERAÇÃO EDITORIAL</div>
        <nav aria-label="Navegação principal">
          {(Object.keys(pages) as Page[]).map((key) => {
            const Icon = icons[key];
            return (
              <button
                key={key}
                className={page === key ? 'active' : ''}
                aria-current={page === key ? 'page' : undefined}
                onClick={() => navigate(key)}
              >
                <Icon size={19} />
                <span>{pages[key]}</span>
                {key === 'calendar' && pending.length > 0 && <b>{pending.length}</b>}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <div className="connection-summary">
            <strong>
              <span className={`dot${data.integration.status === 'connected' ? '' : ' pending'}`} />
              Meta · {metaLabel(data.integration)}
            </strong>
            <p>
              {data.integration.identity
                ? readingAccounts
                  ? 'Lendo contas autorizadas…'
                  : `${data.integration.accounts?.length ?? 0} contas autorizadas`
                : 'Seu acervo e planejamento estão disponíveis neste espaço.'}
            </p>
            <button className="text-button" onClick={() => navigate('connections')}>
              Ver conexões <ArrowRight size={13} />
            </button>
          </div>
          <div className="local-state">
            <span className="dot" />
            {data.mode !== 'local' ? 'Espaço da equipe' : 'Espaço local'}
          </div>
        </div>
      </aside>
      <div className="main-wrap">
        <header className="topbar">
          <span>
            Aplicativo.Net <ChevronRight size={13} /> <strong>{pages[page]}</strong>
          </span>
          <div>
            <span className="timezone">São Paulo · UTC−3</span>
            {session?.user && (
              <button
                className="operator-logout"
                onClick={() => action('/api/logout', {}, 'Sessão encerrada.')}
              >
                Sair
              </button>
            )}
            <div className="operator">
              <span className="avatar yellow">
                {(session?.user?.name || 'Reinaldo Ferreira')
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((part) => part[0])
                  .filter((_, index, all) => index === 0 || index === all.length - 1)
                  .join('')
                  .toUpperCase()}
              </span>
              <div>
                <strong>{session?.user?.name || 'Reinaldo Ferreira'}</strong>
                <small>{session?.user ? 'Perfil autenticado' : 'Founder · operador local'}</small>
              </div>
            </div>
            <Badge tone="outline">
              <span className="dot" />
              {data.mode !== 'local' ? 'Dados da equipe' : 'Dados locais'}
            </Badge>
          </div>
        </header>
        <main className="main">
          {!isCampaignDetail && (
            <div className="page-heading">
              <div>
                <h1>{pages[page]}</h1>
                <p>
                  {
                    {
                      overview: `${new Date(data.today + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · operação editorial`,
                      campaigns: 'Objetivos, peças e responsáveis no mesmo lugar.',
                      content: 'Da primeira ideia à publicação, com contexto em cada etapa.',
                      calendar: 'A linha editorial vigente e as próximas decisões de agenda.',
                      metrics: 'Leituras da Meta, por conta e período.',
                      publications: 'Das publicações do acervo aos resultados por peça e campanha.',
                      connections: 'Seu acervo, suas contas e as permissões de leitura.',
                    }[page]
                  }
                </p>
              </div>
              {data.permissions.edit &&
                page !== 'connections' &&
                page !== 'metrics' &&
                !(page === 'content' && showReconciliation) &&
                page !== 'publications' && (
                  <button
                    className="primary"
                    onClick={() => open({ kind: page === 'campaigns' ? 'campaign' : 'content' })}
                  >
                    <Plus size={17} />
                    {page === 'campaigns' ? 'Nova campanha' : 'Nova pauta'}
                  </button>
                )}
            </div>
          )}
          {error && !modal && (
            <div className="notice error" role="alert">
              <CircleAlert size={18} />
              {error}
              <button
                className="text-button"
                onClick={() =>
                  refresh()
                    .then(() => setError(''))
                    .catch((e) => setError(e.message))
                }
              >
                Atualizar dados
              </button>
            </div>
          )}
          {page === 'overview' && (
            <Dashboard
              data={data}
              workQueue={data.workQueue}
              openWorkItem={(item) => {
                const target = item.target;
                if (target.page === 'campaigns') {
                  openCampaignWorkspace(
                    target.campaignId,
                    target.tab,
                    target.packageId,
                    target.automationId,
                  );
                } else if (target.page === 'connections') {
                  navigate('connections');
                } else if (target.page === 'content') {
                  const content = data.contents.find((value) => value.id === target.contentId);
                  if (content) open({ kind: 'content', item: content });
                } else {
                  const next = `#calendar?proposal=${encodeURIComponent(target.proposalId)}`;
                  location.hash = next;
                  setRoute(next);
                  setPage('calendar');
                  setError('');
                }
              }}
              busy={busy}
              openContent={(item) => open({ kind: 'content', item })}
              openCampaign={(item) => openCampaignWorkspace(item.id)}
              navigate={navigate}
              showUnassigned={() => {
                setSearch('');
                setCampaign('');
                setFormat('');
                setOwner('none');
                setStage('production');
                navigate('content');
              }}
              refreshArchive={() => action('/api/import', {}, 'Acervo atualizado.', 'POST', false)}
            />
          )}
          {page === 'campaigns' && selectedCampaign && (
            <CampaignWorkspace
              key={selectedCampaign.id}
              campaign={selectedCampaign}
              initialTab={new URLSearchParams(route.split('?')[1] ?? '').get('tab') ?? undefined}
              initialPackageId={
                new URLSearchParams(route.split('?')[1] ?? '').get('packageId') ?? undefined
              }
              initialAutomationId={
                new URLSearchParams(route.split('?')[1] ?? '').get('automationId') ?? undefined
              }
              data={data}
              onBack={() => navigate('campaigns')}
              onEdit={() => open({ kind: 'campaign', item: selectedCampaign })}
              onContent={(item) => open({ kind: 'content', item })}
              onNewContent={() => open({ kind: 'content', campaignId: selectedCampaign.id })}
              onNewCampaign={() => open({ kind: 'campaign' })}
              onAddPublication={() => {
                location.hash = `#publications?campaign=${encodeURIComponent(selectedCampaign.id)}&add=1`;
              }}
              collectionSettings={
                <CollectionSettings campaignId={selectedCampaign.id} data={data} />
              }
              refresh={refresh}
            />
          )}
          {page === 'campaigns' && !selectedCampaign && (
            <div className="campaign-grid">
              {[...data.campaigns]
                .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
                .map((c) => {
                  const items = data.contents.filter((i) => i.campaignIds.includes(c.id));
                  const done = items.filter((item) => publishedContentIds.has(item.id)).length;
                  return (
                    <section key={c.id} className="panel campaign-card">
                      <div className="section-heading">
                        <span className="icon-box">
                          <Flag size={20} />
                        </span>
                        <Badge tone={c.status === 'ativa' ? 'publicado' : ''}>
                          {
                            {
                              planejada: 'Planejada',
                              ativa: 'Ativa',
                              pausada: 'Pausada',
                              encerrada: 'Encerrada',
                            }[c.status]
                          }
                        </Badge>
                      </div>
                      <h2>{c.name}</h2>
                      <p>{c.objective}</p>
                      {items.length ? (
                        <div className="campaign-progress">
                          <span>
                            {done} de {items.length} {items.length === 1 ? 'peça' : 'peças'} com
                            publicação registrada
                          </span>
                          <strong>
                            {items.length ? Math.round((done / items.length) * 100) : 0}%
                          </strong>
                          <div>
                            <i
                              style={{
                                width: `${items.length ? (done / items.length) * 100 : 0}%`,
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="campaign-start-state">
                          <strong>Sem conteúdo no planejamento</strong>
                          <span>Abra para acompanhar publicações ou planejar conteúdo.</span>
                        </div>
                      )}
                      <dl>
                        <div>
                          <dt>Período</dt>
                          <dd>
                            {dateLabel(c.startDate)} — {dateLabel(c.endDate)}
                          </dd>
                        </div>
                        <div>
                          <dt>Responsável</dt>
                          <dd>{c.owner || 'A definir'}</dd>
                        </div>
                      </dl>
                      <div className="card-actions">
                        <a
                          className="button primary"
                          href={`#campaigns?id=${encodeURIComponent(c.id)}`}
                        >
                          Abrir campanha
                        </a>
                        {data.permissions.edit && (
                          <button
                            className="text-button"
                            onClick={() => open({ kind: 'campaign', item: c })}
                          >
                            Editar estratégia
                          </button>
                        )}
                      </div>
                      <small className="provenance">
                        {c.strategyReviewRequired
                          ? 'Fonte atualizada · revisar estratégia'
                          : c.source
                            ? 'Estratégia vinculada ao acervo'
                            : 'Campanha criada neste painel'}
                      </small>
                    </section>
                  );
                })}
              {data.campaigns.length > 0 && (
                <p className="campaign-scope-note">
                  As contagens consideram peças vinculadas à campanha. Publicações por conta e
                  vínculos específicos estão em Resultados.
                </p>
              )}
              {!data.campaigns.length && (
                <Empty title="Comece pela campanha">
                  Defina objetivo, público e CTA para organizar suas peças.
                </Empty>
              )}
            </div>
          )}
          {page === 'content' && showReconciliation && (
            <>
              <button onClick={() => setShowReconciliation(false)}>
                <ChevronLeft size={16} /> Voltar aos conteúdos
              </button>
              <ReconciliationWorkspace
                data={data}
                onChanged={async () => {
                  await refresh();
                }}
                openContent={(id) => {
                  const item = data.contents.find((content) => content.id === id);
                  if (item) open({ kind: 'content', item });
                }}
              />
            </>
          )}
          {page === 'content' && !showReconciliation && (
            <>
              {filters}
              <div className="view-toolbar">
                <button onClick={() => setShowReconciliation(true)}>
                  <Link2 size={16} /> Conciliar com o acervo
                </button>
                <span>
                  {filtered.length} conteúdo{filtered.length !== 1 ? 's' : ''}
                </span>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={showDiscarded}
                    onChange={(e) => setShowDiscarded(e.target.checked)}
                  />
                  Incluir descartados
                </label>
                <div className="segmented">
                  <button
                    className={board ? 'selected' : ''}
                    aria-label="Visualizar quadro"
                    aria-pressed={board}
                    onClick={() => setBoard(true)}
                  >
                    <LayoutGrid size={16} />
                    Quadro
                  </button>
                  <button
                    className={!board ? 'selected' : ''}
                    aria-label="Visualizar lista"
                    aria-pressed={!board}
                    onClick={() => setBoard(false)}
                  >
                    <List size={16} />
                    Lista
                  </button>
                </div>
              </div>
              {!filtered.length ? (
                <Empty title="Nenhum conteúdo encontrado">
                  Ajuste os filtros ou crie uma nova pauta.
                </Empty>
              ) : board ? (
                <div className="board">
                  {stages
                    .filter((st) => st !== 'descartado' || showDiscarded || stage === 'descartado')
                    .filter((st) => st !== 'medido' || filtered.some((c) => c.stage === 'medido'))
                    .map((st) => (
                      <section className="column" key={st}>
                        <div className="column-title">
                          <span className={`stage-dot ${st}`} />
                          <h2>{stageLabels[st]}</h2>
                          <b>{filtered.filter((c) => c.stage === st).length}</b>
                        </div>
                        {filtered.filter((c) => c.stage === st).map((c) => contentCard(c))}
                        {!filtered.some((c) => c.stage === st) && (
                          <div className="column-empty">Sem peças nesta etapa</div>
                        )}
                      </section>
                    ))}
                </div>
              ) : (
                <div className="panel list-panel">
                  {filtered.map((item) => (
                    <button
                      key={item.id}
                      className="content-row"
                      onClick={() => open({ kind: 'content', item })}
                    >
                      <span className="row-icon">
                        {item.format === 'Carrossel' ? (
                          <LayoutGrid size={20} />
                        ) : (
                          <Video size={20} />
                        )}
                      </span>
                      <span className="row-title">
                        <small>
                          {item.flgId ?? 'PAUTA LOCAL'} · {item.format}
                        </small>
                        <strong>{item.title}</strong>
                      </span>
                      <StageBadge stage={item.stage} />
                      <span>{dateLabel(dateOf(item))}</span>
                      <ChevronRight size={16} />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {page === 'calendar' && (
            <>
              {filters}
              <label className="check calendar-discarded">
                <input
                  type="checkbox"
                  checked={showDiscarded}
                  onChange={(e) => setShowDiscarded(e.target.checked)}
                />
                Incluir descartados na versão consultada
              </label>
              <CalendarWorkspace
                data={data}
                open={open}
                filters={{ search, campaign, format, stage, owner, showDiscarded }}
              />
              <ProposalList
                data={data}
                action={action}
                busy={busy}
                focusId={new URLSearchParams(route.split('?')[1]).get('proposal') ?? undefined}
              />
            </>
          )}
          {page === 'metrics' && (
            <Metrics integration={data.integration} csrf={data.csrf} refresh={refresh} />
          )}
          {page === 'publications' && <Publications key={route} data={data} refresh={refresh} />}
          {page === 'connections' && (
            <div className="connections-grid">
              <section className="panel">
                <span className="connection-symbol">FLG</span>
                <div className="section-heading">
                  <h2>Acervo local</h2>
                  <Badge tone="publicado">Disponível</Badge>
                </div>
                <p>
                  Campanhas, fichas e versões do calendário importadas com referência à fonte. A
                  atualização preserva suas observações e registra conflitos.
                </p>
                <dl>
                  <div>
                    <dt>Última conferência</dt>
                    <dd>{data.sync ? stamp(data.sync.createdAt) : 'Pendente'}</dd>
                  </div>
                  <div>
                    <dt>Fontes</dt>
                    <dd>{data.sync?.sources ?? 0} arquivos</dd>
                  </div>
                </dl>
                <button
                  disabled={busy}
                  onClick={() =>
                    action(
                      '/api/import',
                      {},
                      'Importação concluída. As fontes foram conferidas.',
                      'POST',
                      false,
                    )
                  }
                >
                  <RefreshCw size={16} className={busy ? 'spin' : ''} />
                  Atualizar acervo
                </button>
              </section>
              <MetaConnection
                integration={data.integration}
                csrf={data.csrf}
                refresh={refresh}
                readingAccounts={readingAccounts}
                connectionAlerts={data.workQueue?.items.filter(
                  (item) => item.kind === 'connection',
                )}
              />
              <TeamSettings data={data} refresh={refresh} />
              <ExportChangesWorkspace data={data} refresh={refresh} />
            </div>
          )}
        </main>
        <footer className="app-footer">
          <span>Aplicativo.Net · Founder Led Growth</span>
          <span>Reinaldo Ferreira · founder e voz da marca</span>
        </footer>
      </div>
      {toast && (
        <div className="toast" role="status">
          <Check size={17} />
          {toast}
          <button aria-label="Fechar aviso" onClick={() => setToast('')}>
            <X size={16} />
          </button>
        </div>
      )}
      {modal && (
        <Dialog
          drawer={modal.kind === 'content' || modal.kind === 'source' || modal.kind === 'campaign'}
          title={
            modal.kind === 'campaign'
              ? modal.item
                ? 'Campanha'
                : 'Nova campanha'
              : modal.kind === 'content'
                ? modal.item
                  ? 'Detalhes do conteúdo'
                  : 'Nova pauta'
                : modal.kind === 'date'
                  ? 'Propor uma data'
                  : modal.kind === 'publication'
                    ? 'Registrar publicação'
                    : modal.kind === 'history'
                      ? modal.reference
                        ? 'Calendário do acervo'
                        : 'Calendário histórico'
                      : 'Fonte no acervo'
          }
          onClose={() => {
            if (!busy) {
              setModal(undefined);
              setError('');
            }
          }}
        >
          {error && (
            <div className="notice error" role="alert">
              <CircleAlert size={18} />
              {error}
              <button
                className="text-button"
                onClick={() =>
                  refresh()
                    .then(() => {
                      setModal(undefined);
                      setError('');
                    })
                    .catch((e) => setError(e.message))
                }
              >
                Atualizar dados
              </button>
            </div>
          )}
          {modal.kind === 'campaign' && (
            <CampaignForm
              data={data}
              item={modal.item}
              busy={busy}
              onSource={(source) => open({ kind: 'source', source })}
              submit={async (body) => {
                const creating = !modal.item;
                const saved = await action(
                  `/api/campaigns${modal.item ? '/' + encodeURIComponent(modal.item.id) : ''}`,
                  body,
                  `Campanha “${(body as { name: string }).name}” ${creating ? 'criada' : 'atualizada'}.`,
                  modal.item ? 'PUT' : 'POST',
                );
                if (creating && saved?.id) openCampaignWorkspace(saved.id);
              }}
            />
          )}
          {modal.kind === 'content' && (
            <ContentForm
              defaultCampaignId={modal.campaignId}
              item={modal.item}
              data={data}
              busy={busy}
              date={modal.item ? dateOf(modal.item) : ''}
              open={open}
              viewMetrics={() => {
                setModal(undefined);
                location.hash = `publications?content=${encodeURIComponent(modal.item?.id || '')}`;
              }}
              action={action}
              submit={(body) =>
                action(
                  `/api/contents${modal.item ? '/' + encodeURIComponent(modal.item.id) : ''}`,
                  body,
                  'Conteúdo salvo.',
                  modal.item ? 'PUT' : 'POST',
                )
              }
            />
          )}
          {modal.kind === 'date' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                action(
                  '/api/proposals',
                  {
                    contentId: s(f, 'contentId'),
                    targetDate: s(f, 'targetDate'),
                    reason: s(f, 'reason'),
                    baseHash: current?.source.hash ?? null,
                  },
                  'Proposta criada para revisão do planejamento.',
                );
              }}
            >
              <p className="notice">
                A aprovação aparece no planejamento operacional, como simulação. O calendário
                vigente só muda depois do registro no acervo e da reimportação. Publicar na rede
                social é uma ação separada.
              </p>
              <div className="form-grid">
                <Field label="Conteúdo" wide>
                  <select name="contentId" defaultValue={modal.item?.id ?? ''} required>
                    <option value="" disabled>
                      Selecione uma peça
                    </option>
                    {eligible.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.flgId ? c.flgId + ' · ' : ''}
                        {c.title}
                      </option>
                    ))}
                  </select>
                </Field>
                {modal.item && (
                  <div className="wide compare-date">
                    <span>
                      Planejamento atual<strong>{dateLabel(dateOf(modal.item), true)}</strong>
                    </span>
                    <ArrowRight size={20} />
                    <span>
                      Nova data<strong>Proposta para revisão</strong>
                    </span>
                  </div>
                )}
                {modal.item && (
                  <p className="fine-print wide">
                    Acervo vigente:{' '}
                    {dateLabel(
                      current?.entries.find((entry) => entry.contentId === modal.item?.id)?.date ??
                        '',
                      true,
                    )}
                    .
                  </p>
                )}
                <Field label="Data proposta" wide>
                  <input required type="date" name="targetDate" defaultValue={modal.date ?? ''} />
                </Field>
                <Field label="Motivo da alteração" wide>
                  <textarea
                    required
                    minLength={3}
                    maxLength={12000}
                    name="reason"
                    placeholder="O que motivou esta mudança?"
                  />
                </Field>
              </div>
              <FormFooter busy={busy} label="Enviar proposta" />
            </form>
          )}
          {modal.kind === 'publication' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                action(
                  `/api/contents/${encodeURIComponent(modal.item.id)}/publications`,
                  {
                    version: modal.item.version,
                    account: s(f, 'account'),
                    channel: s(f, 'channel'),
                    date: s(f, 'date'),
                    url: s(f, 'url'),
                    evidence: s(f, 'evidence'),
                  },
                  'Publicação registrada com evidência. Registro no acervo pendente.',
                );
              }}
            >
              <p className="notice">
                Registre somente uma publicação já realizada. Cada destino tem seu próprio registro;
                colaboradores do mesmo post não são novas publicações.
              </p>
              <h3>{modal.item.title}</h3>
              <div className="form-grid">
                <Field label="Conta / perfil">
                  <input required name="account" maxLength={250} placeholder="@perfil" />
                </Field>
                <Field label="Canal">
                  <select name="channel">
                    {['Instagram', 'Facebook', 'YouTube', 'Outro'].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Data real · São Paulo">
                  <input
                    required
                    type="date"
                    name="date"
                    max={data.today}
                    defaultValue={data.today}
                  />
                </Field>
                <Field label="Link HTTPS da publicação">
                  <input required type="url" name="url" placeholder="https://…" />
                </Field>
                <Field label="Evidência e observações" wide>
                  <textarea
                    name="evidence"
                    required
                    minLength={3}
                    maxLength={12000}
                    placeholder="Onde e como a publicação foi conferida?"
                  />
                </Field>
              </div>
              <FormFooter busy={busy} label="Confirmar registro manual" />
            </form>
          )}
          {modal.kind === 'source' && <SourceViewer source={modal.source} />}
          {modal.kind === 'history' && (
            <>
              <Badge>{modal.label}</Badge>
              <h2>{modal.entry.title}</h2>
              <p>
                {dateLabel(modal.entry.date, true)} · {modal.entry.format}
              </p>
              <p className="notice">
                {modal.reference
                  ? 'Referência vigente importada do acervo. Uma alteração exige proposta, registro na fonte e reimportação.'
                  : 'Versão histórica preservada. A data e a etapa desta versão não representam a execução atual.'}
              </p>
            </>
          )}
        </Dialog>
      )}
    </div>
  );
}

function Dialog({
  drawer = false,
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  drawer?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current!;
    d.showModal();
    return () => d.close();
  }, []);
  return (
    <dialog
      className={drawer ? 'detail-drawer' : undefined}
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-labelledby="dialog-title"
    >
      <div className="dialog-head">
        <h2 id="dialog-title">{title}</h2>
        <button aria-label="Fechar" onClick={onClose}>
          <X size={20} />
        </button>
      </div>
      <div className="dialog-body">{children}</div>
    </dialog>
  );
}
function FormFooter({
  busy,
  disabled = false,
  label = 'Salvar alterações',
}: {
  busy: boolean;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <div className="form-footer">
      <span>
        {disabled ? 'Consulta sem permissão para editar' : 'Confirme para salvar as alterações'}
      </span>
      <button className="primary" disabled={busy || disabled} type="submit">
        {busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />}{' '}
        {busy ? 'Salvando…' : label}
      </button>
    </div>
  );
}
function CampaignForm({
  data,
  item,
  busy,
  submit,
  onSource,
}: {
  data: State;
  item?: Campaign;
  busy: boolean;
  submit: (body: unknown) => void;
  onSource: (source: SourceRef) => void;
}) {
  const frozen = !data.permissions.edit;
  function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    submit({
      ...(item ? { version: item.version } : {}),
      name: s(f, 'name'),
      objective: s(f, 'objective'),
      audience: s(f, 'audience'),
      cta: s(f, 'cta'),
      owner: s(f, 'owner'),
      approver: s(f, 'approver'),
      sourceReviewConfirmed: f.has('sourceReviewConfirmed'),
      approverMemberId: data.permissions.admin ? s(f, 'approverMemberId') : item?.approverMemberId,
      startDate: s(f, 'startDate'),
      endDate: s(f, 'endDate'),
      status: s(f, 'status'),
      notes: s(f, 'notes'),
    });
  }
  return (
    <form onSubmit={save}>
      {item?.source && (
        <p className="notice">
          {item?.strategyReviewRequired && (
            <strong>A fonte mudou desde o resumo inicial. Consulte a nova revisão. </strong>
          )}
          A estratégia pode ser revisada aqui. O documento de origem e as versões anteriores
          permanecem preservados.{' '}
          <button type="button" className="text-button" onClick={() => onSource(item!.source!)}>
            Ler fonte <ArrowRight size={13} />
          </button>
        </p>
      )}
      <div className="form-grid">
        <Field label="Nome da campanha" wide>
          <input name="name" required maxLength={250} defaultValue={item?.name} readOnly={frozen} />
        </Field>
        <Field label="Objetivo" wide>
          <textarea
            name="objective"
            required
            maxLength={12000}
            defaultValue={item?.objective}
            readOnly={frozen}
          />
        </Field>
        <Field label="Público">
          <textarea
            name="audience"
            maxLength={12000}
            defaultValue={item?.audience}
            readOnly={frozen}
          />
        </Field>
        <Field label="Chamada para ação (CTA)">
          <textarea name="cta" maxLength={12000} defaultValue={item?.cta} readOnly={frozen} />
        </Field>
        <Field label="Início">
          <input name="startDate" type="date" defaultValue={item?.startDate} readOnly={frozen} />
        </Field>
        <Field label="Fim">
          <input name="endDate" type="date" defaultValue={item?.endDate} readOnly={frozen} />
        </Field>
        <Field label="Responsável">
          <input name="owner" maxLength={250} defaultValue={item?.owner} placeholder="A definir" />
        </Field>
        <Field label="Aprovador responsável">
          <select
            name="approverMemberId"
            defaultValue={item?.approverMemberId ?? ''}
            disabled={!data.permissions.admin}
          >
            <option value="">Administrador do FLG</option>
            {data.members
              .filter((m) => m.active && ['admin', 'approver'].includes(m.role))
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
          <input type="hidden" name="approver" value={item?.approver ?? 'Administrador do FLG'} />
          <small>Apenas o administrador designa quem pode aprovar.</small>
        </Field>
        <Field label="Situação">
          <select name="status" defaultValue={item?.status ?? 'planejada'}>
            {['planejada', 'ativa', 'pausada', 'encerrada'].map((st) => (
              <option key={st}>{st}</option>
            ))}
          </select>
        </Field>
        <Field label="Observações operacionais" wide>
          <textarea name="notes" maxLength={12000} defaultValue={item?.notes} />
        </Field>
      </div>
      {item?.strategyReviewRequired && (
        <label className="check">
          <input required type="checkbox" name="sourceReviewConfirmed" />
          Li a nova fonte e revisei a estratégia desta campanha.
        </label>
      )}
      <FormFooter
        busy={busy}
        disabled={!data.permissions.edit}
        label={item ? 'Salvar campanha' : 'Criar campanha'}
      />
    </form>
  );
}
function ContentForm({
  defaultCampaignId,
  item,
  data,
  busy,
  date,
  submit,
  open,
  action,
  viewMetrics,
}: {
  defaultCampaignId?: string;
  item?: Content;
  data: State;
  busy: boolean;
  date: string;
  submit: (body: unknown) => void;
  open: (modal: Modal) => void;
  viewMetrics: () => void;
  action: (
    path: string,
    body: unknown,
    message: string,
    method?: string,
    close?: boolean,
  ) => Promise<void>;
}) {
  const [selectedStage, setSelectedStage] = useState<Stage>(item?.stage ?? 'pauta');
  const [reason, setReason] = useState('');
  const pubs = data.publications.filter((p) => p.contentId === item?.id);
  const operationalChange = item?.sourceValues && item.sourceValues.stage !== item.stage;
  const save = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    submit({
      ...(item ? { version: item.version } : {}),
      title: s(f, 'title'),
      format: s(f, 'format'),
      stage: s(f, 'stage'),
      owner: s(f, 'owner'),
      brief: s(f, 'brief'),
      notes: s(f, 'notes'),
      campaignIds: f.getAll('campaignIds'),
      reserve: f.has('reserve'),
      stageEvidence: s(f, 'stageEvidence'),
    });
  };
  return (
    <>
      <div className="detail-meta">
        <Badge tone="yellow">{item?.flgId ?? 'PAUTA · ID LOCAL'}</Badge>
        {item && <StageBadge stage={item.stage} />}
        <span>{item?.format ?? 'Novo conteúdo'}</span>
      </div>
      {item?.conflict && (
        <div className="notice conflict">
          <h3>Etapa mudou no acervo</h3>
          <p>
            Base anterior: {stageLabels[item.conflict.previous]}. Acervo:{' '}
            {stageLabels[item.conflict.incoming]}. Painel: {stageLabels[item.conflict.local]}.
          </p>
          <label>
            Motivo da decisão
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explique qual estado deve prevalecer"
            />
          </label>
          <div className="card-actions">
            {(['acervo', 'local'] as const).map((choice) => (
              <button
                key={choice}
                disabled={busy || reason.trim().length < 3}
                onClick={() =>
                  action(
                    `/api/contents/${encodeURIComponent(item.id)}/resolve`,
                    { version: item.version, choice, reason },
                    'Conflito resolvido.',
                  )
                }
              >
                {choice === 'acervo' ? 'Usar etapa do acervo' : 'Manter etapa local'}
              </button>
            ))}
          </div>
        </div>
      )}
      {item?.sourceMissing && (
        <p className="notice error">
          A fonte deixou de aparecer na importação. O registro foi preservado para revisão.
        </p>
      )}
      {operationalChange && (
        <p className="notice">
          Etapa operacional alterada no painel · registro no acervo pendente.
        </p>
      )}
      <form onSubmit={save}>
        <div className="form-grid">
          <Field label="Título" wide>
            <input
              name="title"
              defaultValue={item?.title}
              required
              maxLength={250}
              readOnly={!!item?.source}
            />
          </Field>
          <Field label="Formato">
            <select name="format" defaultValue={item?.format ?? 'Reel'}>
              {(item?.source ? [item.format] : formats).map((f) => (
                <option key={f}>{f}</option>
              ))}
            </select>
          </Field>
          <Field label="Etapa de produção">
            <select
              name="stage"
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value as Stage)}
            >
              {stages
                .filter((st) => !['publicado', 'medido'].includes(st) || item?.stage === st)
                .map((st) => (
                  <option
                    key={st}
                    value={st}
                    disabled={
                      !!item && ['publicado', 'medido'].includes(item.stage) && st !== item.stage
                    }
                  >
                    {stageLabels[st]}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Responsável">
            <input
              name="owner"
              maxLength={250}
              defaultValue={item?.owner}
              placeholder="A definir"
            />
          </Field>
          <div className="field">
            <span>Data vigente</span>
            <div className="date-field">
              <CalendarDays size={16} />
              {dateLabel(date, true)}
            </div>
          </div>
          <Field label="Briefing / proposta de texto" wide>
            <textarea
              name="brief"
              maxLength={12000}
              rows={4}
              defaultValue={item?.brief}
              placeholder="Ideia central, abordagem e chamada para ação. O texto final do acervo permanece separado."
            />
          </Field>
          <fieldset className="wide">
            <legend>Campanhas vinculadas</legend>
            {data.campaigns.length ? (
              data.campaigns.map((c) => (
                <label key={c.id} className="check">
                  <input
                    type="checkbox"
                    name="campaignIds"
                    value={c.id}
                    defaultChecked={
                      item ? item.campaignIds.includes(c.id) : c.id === defaultCampaignId
                    }
                  />
                  {c.name}
                </label>
              ))
            ) : (
              <p className="muted">Crie uma campanha para vincular esta peça.</p>
            )}
          </fieldset>
          <label className="check wide">
            <input type="checkbox" name="reserve" defaultChecked={item?.reserve} />
            Conteúdo de reserva
          </label>
          <Field label="Evidência da etapa / motivo" wide>
            <textarea
              name="stageEvidence"
              required={
                ['gravado', 'editado', 'descartado'].includes(selectedStage) &&
                selectedStage !== item?.stage
              }
              maxLength={12000}
              defaultValue={item?.stageEvidence}
              placeholder="Referência ao material, conferência humana ou motivo do descarte."
            />
          </Field>
          <Field label="Observações" wide>
            <textarea name="notes" maxLength={12000} defaultValue={item?.notes} />
          </Field>
        </div>
        <FormFooter
          busy={busy}
          disabled={!data.permissions.edit}
          label={item ? 'Salvar conteúdo' : 'Criar pauta'}
        />
      </form>
      {item && (
        <section className="detail-actions">
          <h3>Ações e referências</h3>
          <p className="fine-print">Salve suas edições antes de abrir outra ação.</p>
          <div className="card-actions">
            {['Reel', 'Carrossel', 'Stories', 'Post'].includes(item.format) &&
              ['publicado', 'medido'].includes(item.stage) && (
                <button onClick={viewMetrics}>
                  <BarChart3 size={16} />
                  Publicação e métricas
                </button>
              )}
            {data.permissions.edit &&
              !['publicado', 'medido', 'descartado'].includes(item.stage) && (
                <button onClick={() => open({ kind: 'date', item })}>
                  <CalendarDays size={16} />
                  Propor data
                </button>
              )}
            {data.permissions.edit && item.stage !== 'descartado' && (
              <button onClick={() => open({ kind: 'publication', item })}>
                <CheckCheck size={16} />
                Registrar publicação
              </button>
            )}
            {item.source && (
              <button onClick={() => open({ kind: 'source', source: item.source! })}>
                <FileText size={16} />
                Ler fonte do acervo
              </button>
            )}
          </div>
          {item.source && (
            <p className="fine-print">
              Etapa na fonte: {stageLabels[item.sourceValues?.stage ?? item.stage]}. Conteúdo
              importado em {stamp(item.source.importedAt)}.
            </p>
          )}
          {item.source &&
            ['publicado', 'medido'].includes(item.sourceValues?.stage ?? 'pauta') &&
            !pubs.length && (
              <p className="notice">
                A ficha registra a publicação. Consulte a fonte para contas, horários e evidências;
                os destinos históricos não foram inferidos automaticamente.
              </p>
            )}
          {pubs.map((p) => (
            <article className="publication" key={p.id}>
              <Badge tone="publicado">
                {p.source === 'meta' ? 'Conferido na Meta' : publicationOrigin(p).label}
              </Badge>
              <strong>
                {p.account} · {p.channel}
              </strong>
              <span>
                {p.publishedAt
                  ? `${new Date(p.publishedAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} · São Paulo · precisão de segundo`
                  : `${dateLabel(p.date, true)} · precisão de dia`}
              </span>
              {publicationOrigin(p).simulated ? (
                <span>Endereço simulado, sem publicação real: {p.url}</span>
              ) : (
                <a href={p.url} target="_blank" rel="noreferrer">
                  Ver publicação <ArrowRight size={13} />
                </a>
              )}
              <p>{p.evidence}</p>
              <small>
                {p.source === 'meta'
                  ? 'Vínculo operacional no painel · fonte original preservada'
                  : publicationOrigin(p).simulated
                    ? 'Confirmada pelo gateway simulado da etapa D · nada foi publicado'
                    : 'Registro no acervo pendente'}
              </small>
            </article>
          ))}
        </section>
      )}
    </>
  );
}
function SourceViewer({ source }: { source: SourceRef }) {
  const [value, setValue] = useState<SourceRevision>();
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setValue(undefined);
    fetch(`/api/source?${new URLSearchParams({ path: source.path, hash: source.hash })}`, {
      signal: controller.signal,
    })
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(body.error);
        setValue(body);
      })
      .catch((e) => {
        if (e.name !== 'AbortError') setError(e.message);
      });
    return () => controller.abort();
  }, [source.path, source.hash]);
  return (
    <>
      <p className="source-path">{source.path}</p>
      <p className="fine-print">
        Revisão {source.hash.slice(0, 12)} · importada em {stamp(source.importedAt)} · somente
        leitura
      </p>
      {error ? (
        <p role="alert">{error}</p>
      ) : value ? (
        <pre className="source-text">{value.text}</pre>
      ) : (
        <p>Carregando fonte…</p>
      )}
    </>
  );
}
function ProposalList({
  data,
  action,
  busy,
  focusId,
}: {
  data: State;
  action: (
    path: string,
    body: unknown,
    message: string,
    method?: string,
    close?: boolean,
  ) => Promise<void>;
  busy: boolean;
  focusId?: string;
}) {
  const [history, setHistory] = useState(false);
  const focusRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!focusId) return;
    focusRef.current?.scrollIntoView({ block: 'center' });
    focusRef.current?.focus({ preventScroll: true });
  }, [focusId]);
  const proposals = data.proposals.filter(
    (p) => history || p.id === focusId || ['pendente', 'conflito', 'aprovada'].includes(p.status),
  );
  return (
    <section className="section">
      <div className="section-heading">
        <div>
          <h2>Propostas de calendário</h2>
          <p>A decisão fica registrada, com sua origem e motivo.</p>
        </div>
        <label className="check">
          <input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} />
          Mostrar histórico
        </label>
      </div>
      {!proposals.length ? (
        <div className="proposal-empty">
          <CheckCheck size={20} />
          <div>
            <strong>Nenhuma proposta em aberto</strong>
            <p>Use “Propor data” ou arraste uma peça no calendário vigente.</p>
          </div>
        </div>
      ) : (
        proposals
          .slice()
          .reverse()
          .map((p) => (
            <article
              className="panel proposal"
              key={p.id}
              ref={p.id === focusId ? focusRef : undefined}
              tabIndex={p.id === focusId ? -1 : undefined}
            >
              <div>
                <Badge
                  tone={p.status === 'conflito' ? 'error' : p.status === 'aprovada' ? 'yellow' : ''}
                >
                  {p.status === 'aprovada' && !data.schedules.some((s) => s.proposalId === p.id)
                    ? 'Aprovada anteriormente · registro no acervo pendente'
                    : proposalLabels[p.status]}
                </Badge>
                <h3>{data.contents.find((c) => c.id === p.contentId)?.title ?? p.contentId}</h3>
                <div className="date-comparison">
                  {dateLabel(p.baseDate, true)}
                  <ArrowRight size={15} />
                  <strong>{dateLabel(p.targetDate, true)}</strong>
                </div>
                <p>{p.reason}</p>
                {p.decisionNote && <small>{p.decisionNote}</small>}
              </div>
              {data.permissions.approve &&
                !['rejeitada', 'incorporada', 'substituida', 'aprovada', 'executada'].includes(
                  p.status,
                ) && (
                  <div className="proposal-actions">
                    {p.status === 'pendente' && (
                      <button
                        className="primary"
                        disabled={busy}
                        onClick={() =>
                          action(
                            `/api/proposals/${p.id}/decision`,
                            { version: p.version, decision: 'aprovada', note: '' },
                            'Data aprovada para o planejamento operacional; registro no acervo pendente.',
                            'POST',
                            false,
                          )
                        }
                      >
                        <Check size={15} />
                        Aprovar proposta
                      </button>
                    )}
                    <button
                      disabled={busy}
                      onClick={() =>
                        action(
                          `/api/proposals/${p.id}/decision`,
                          { version: p.version, decision: 'rejeitada', note: '' },
                          'Proposta rejeitada.',
                          'POST',
                          false,
                        )
                      }
                    >
                      Rejeitar proposta
                    </button>
                  </div>
                )}
            </article>
          ))
      )}
    </section>
  );
}
